import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-5a28f870"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "three-d-browser-heading" }
const _hoisted_2 = ["aria-label"]
const _hoisted_3 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", { class: "icon" }, [
  /*#__PURE__*/_createElementVNode("i", { class: "fas fa-arrow-left" })
], -1))
const _hoisted_4 = [
  _hoisted_3
]
const _hoisted_5 = { class: "title-five" }
const _hoisted_6 = {
  key: 0,
  class: "viewer-shell"
}
const _hoisted_7 = {
  key: 0,
  class: "asset-unavailable",
  role: "status"
}
const _hoisted_8 = {
  key: 1,
  class: "three-d-error",
  role: "alert"
}
const _hoisted_9 = {
  key: 2,
  class: "three-d-tiles"
}
const _hoisted_10 = ["onClick"]
const _hoisted_11 = { class: "thumbnail" }
const _hoisted_12 = ["src", "alt"]
const _hoisted_13 = {
  key: 1,
  class: "fallback-icon icon",
  "aria-hidden": "true"
}
const _hoisted_14 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("i", { class: "fas fa-cube" }, null, -1))
const _hoisted_15 = [
  _hoisted_14
]
const _hoisted_16 = { class: "tile-name" }
const _hoisted_17 = {
  key: 3,
  class: "library-loading-blocker"
}
const _hoisted_18 = {
  class: "loading-status",
  role: "status",
  "aria-live": "polite"
}
const _hoisted_19 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", {
  class: "icon is-large",
  "aria-hidden": "true"
}, [
  /*#__PURE__*/_createElementVNode("i", { class: "fas fa-spinner fa-pulse fa-2x" })
], -1))
const _hoisted_20 = { class: "loading-title" }
const _hoisted_21 = { class: "loading-help" }

import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'
import ThreeDSnapshotViewer from './ThreeDSnapshotViewer.vue'
import type {
  ThreeDViewerPartSearchSourcePayload,
  ThreeDViewerSearchCarryIn
} from '@/interfaces/library'

type SnapshotViewerApi = {
  setPartSearchSource: (payload: ThreeDViewerPartSearchSourcePayload) => boolean
  setViewerPartSearchQuery: (query: string) => void
}

type ViewerEvent = {
  attemptId: number
  snapshotId: number
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ThreeDContentBrowser',
  props: {
    mobileLayout: { type: Boolean }
  },
  setup(__props: any, { expose: __expose }) {

const store = useStore()

const models = computed(() => store.getters['content/threeDModels'])
const selectedModel = computed(() => store.getters['content/selectedThreeDModel'])
const selectedSnapshot = computed(() => store.getters['content/selectedThreeDSnapshot'])
const selectedSnapshotId = computed(() => store.state.content.toc.selectedThreeDSnapshotId)
const viewerSearchCarryIn = computed<ThreeDViewerSearchCarryIn | null>(() =>
  store.state.content.toc.threeDViewerSearchCarryIn ?? null
)
const snapshotViewer = ref<SnapshotViewerApi | null>(null)
const viewerStatus = ref<'awaitingAsset' | 'error' | 'idle' | 'loadingViewer' | 'ready'>('idle')
const viewerAttemptId = ref(0)
let appliedCarryInRequestId: number | null = null

const visibleItems = computed(() => {
  if (!selectedModel.value) {
    return models.value.map((model: any) => ({
      ...model,
      kind: 'model',
      name: model.name
    }))
  }
  return selectedModel.value.snapshots.map((snapshot: any) => ({
    ...snapshot,
    kind: 'snapshot',
    name: snapshot.snapshotName || String(snapshot.id)
  }))
})

function selectItem (item: any) {
  if (item.kind === 'model') store.dispatch('content/selectThreeDModel', item.id)
  else store.dispatch('content/selectThreeDSnapshot', item)
}

function showModels () {
  store.dispatch('content/selectThreeDModel', null)
}

function showSnapshots () {
  if (selectedModel.value) {
    store.dispatch('content/selectThreeDModel', selectedModel.value.id)
  }
}

function isCurrentViewerAttempt ({ attemptId, snapshotId }: ViewerEvent) {
  return attemptId === viewerAttemptId.value &&
    snapshotId === selectedSnapshotId.value
}

function handleViewerError (event: ViewerEvent) {
  if (
    !isCurrentViewerAttempt(event) ||
    !['awaitingAsset', 'loadingViewer'].includes(viewerStatus.value)
  ) return

  viewerStatus.value = 'error'
}

function handleViewerAssetReady (event: ViewerEvent) {
  if (!isCurrentViewerAttempt(event) || viewerStatus.value !== 'awaitingAsset') return

  viewerStatus.value = 'ready'
  applyViewerSearchCarryIn()
}

function handleViewerShellReady (event: ViewerEvent) {
  if (!isCurrentViewerAttempt(event) || viewerStatus.value !== 'loadingViewer') return

  // Snapshot asset delivery is frontend-only until the backend contract exists.
  // That future loader must call ThreeDSnapshotViewer.setAssetReady only after
  // the selected asset is usable; until then the UI exits loading as unavailable.
  viewerStatus.value = 'awaitingAsset'
}

function retryViewer () {
  appliedCarryInRequestId = null
  viewerAttemptId.value++
  viewerStatus.value = 'loadingViewer'
}

function applyViewerSearchCarryIn () {
  const carryIn = viewerSearchCarryIn.value
  if (
    viewerStatus.value !== 'ready' ||
    !snapshotViewer.value ||
    !carryIn ||
    carryIn.snapshotId !== selectedSnapshotId.value ||
    carryIn.requestId === appliedCarryInRequestId
  ) return false

  snapshotViewer.value.setViewerPartSearchQuery(carryIn.query)
  appliedCarryInRequestId = carryIn.requestId
  return true
}

function setPartSearchSource (payload: ThreeDViewerPartSearchSourcePayload) {
  return snapshotViewer.value?.setPartSearchSource(payload) ?? false
}

watch(viewerSearchCarryIn, applyViewerSearchCarryIn)

watch(
  selectedSnapshotId,
  (snapshotId) => {
    appliedCarryInRequestId = null
    if (snapshotId === null) {
      viewerStatus.value = 'idle'
      return
    }

    viewerAttemptId.value++
    viewerStatus.value = 'loadingViewer'
  },
  { flush: 'sync', immediate: true }
)

__expose({ setPartSearchSource })

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", {
    class: _normalizeClass(["three-d-browser", { 'is-mobile-layout': _ctx.mobileLayout }]),
    "data-unit": "three-d-content-browser"
  }, [
    _createElementVNode("div", _hoisted_1, [
      (selectedModel.value)
        ? (_openBlock(), _createElementBlock("button", {
            key: 0,
            class: "button is-white",
            type: "button",
            "aria-label": _ctx.$t('back'),
            onClick: showModels
          }, _hoisted_4, 8, _hoisted_2))
        : _createCommentVNode("", true),
      _createElementVNode("h3", _hoisted_5, _toDisplayString(selectedModel.value ? selectedModel.value.name : _ctx.$t('threeD.content')), 1)
    ]),
    (selectedSnapshot.value && viewerStatus.value !== 'error')
      ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
          (_openBlock(), _createBlock(ThreeDSnapshotViewer, {
            key: `${selectedSnapshot.value.id}-${viewerAttemptId.value}`,
            ref_key: "snapshotViewer",
            ref: snapshotViewer,
            "attempt-id": viewerAttemptId.value,
            "mobile-layout": _ctx.mobileLayout,
            snapshot: selectedSnapshot.value,
            onAssetReady: handleViewerAssetReady,
            onError: handleViewerError,
            onViewerShellReady: handleViewerShellReady
          }, null, 8, ["attempt-id", "mobile-layout", "snapshot"])),
          (viewerStatus.value === 'awaitingAsset')
            ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                _createElementVNode("p", null, _toDisplayString(_ctx.$t('failedToLoad', { content: _ctx.$tc('snapshot', 1) })), 1),
                _createElementVNode("button", {
                  class: "button is-primary",
                  type: "button",
                  onClick: showSnapshots
                }, _toDisplayString(_ctx.$t('back')), 1)
              ]))
            : _createCommentVNode("", true)
        ]))
      : (selectedSnapshot.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
            _createElementVNode("p", null, _toDisplayString(_ctx.$t('failedToLoad', { content: _ctx.$tc('snapshot', 1) })), 1),
            _createElementVNode("button", {
              class: "button is-primary",
              type: "button",
              onClick: retryViewer
            }, _toDisplayString(_ctx.$t('tryAgain')), 1)
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_9, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(visibleItems.value, (item) => {
              return (_openBlock(), _createElementBlock("button", {
                key: `${item.kind}-${item.id}`,
                class: _normalizeClass(["three-d-tile", { 'is-selected': item.kind === 'snapshot' && item.id === selectedSnapshotId.value }]),
                type: "button",
                onClick: ($event: any) => (selectItem(item))
              }, [
                _createElementVNode("span", _hoisted_11, [
                  (item.thumbnailUrl)
                    ? (_openBlock(), _createElementBlock("img", {
                        key: 0,
                        src: item.thumbnailUrl,
                        alt: item.name
                      }, null, 8, _hoisted_12))
                    : (_openBlock(), _createElementBlock("span", _hoisted_13, _hoisted_15))
                ]),
                _createElementVNode("span", _hoisted_16, _toDisplayString(item.name), 1)
              ], 10, _hoisted_10))
            }), 128))
          ])),
    (viewerStatus.value === 'loadingViewer')
      ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
          _createElementVNode("div", _hoisted_18, [
            _hoisted_19,
            _createElementVNode("p", _hoisted_20, _toDisplayString(_ctx.$t('threeD.loadingViewerAsset')), 1),
            _createElementVNode("p", _hoisted_21, _toDisplayString(_ctx.$t('threeD.loadingViewerAssetHelp')), 1)
          ]),
          _createElementVNode("button", {
            class: "button is-light loading-cancel",
            type: "button",
            onClick: showSnapshots
          }, _toDisplayString(_ctx.$t('back')), 1)
        ]))
      : _createCommentVNode("", true)
  ], 2))
}
}

})